import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { neutral, primary } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';

import type { WorkflowStepLocalKey } from '../../_types/workflowBuilder';
import { errorMiddleStepRowSx, middleStepRowSx, selectedMiddleStepRowSx } from './workflowStepFieldStyles';

export interface SortableWorkflowStepRowProps {
  id: WorkflowStepLocalKey;
  title: string;
  selected: boolean;
  reorderMode: boolean;
  hasError?: boolean;
  onSelect: (id: WorkflowStepLocalKey) => void;
  onDelete: (id: WorkflowStepLocalKey) => void;
}

const SortableWorkflowStepRow: React.FC<SortableWorkflowStepRowProps> = ({ id, title, selected, reorderMode, hasError, onSelect, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !reorderMode,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid={`workflow-step-row-${id}`}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        opacity: isDragging ? 0.85 : 1,
      }}
    >
      <Box
        sx={{
          ...(selected
            ? selectedMiddleStepRowSx
            : hasError
            ? errorMiddleStepRowSx
            : middleStepRowSx),
          cursor: 'pointer',
          flex: 1,
          minWidth: 0,
        }}
        onClick={() => onSelect(id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(id);
          }
        }}
        role="button"
        tabIndex={0}
        aria-current={selected ? 'true' : undefined}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 45, px: 2.5, py: 1 }}>
          {reorderMode && (
            <IconButton
              size="small"
              aria-label={t('performanceReporting.reviewWorkflows.new.reorder.dragHandleAria')}
              sx={{ color: neutral[400] }}
              {...listeners}
              {...attributes}
              onClick={(e) => e.stopPropagation()}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          )}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontWeight: 500,
              fontSize: '0.875rem',
              color: neutral[800],
            }}
          >
            {title}
          </Typography>
        </Stack>
      </Box>
      <IconButton
        size="small"
        aria-label={t('performanceReporting.reviewWorkflows.new.steps.deleteStepAria')}
        sx={{
          flexShrink: 0,
          color: primary[800],
          '&:hover': {
            color: primary,
            border: `1px solid ${neutral[200]}`,
            borderRadius: 1,
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(id);
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default SortableWorkflowStepRow;
